import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { baseURL } from '@/config/APIConfig';


const API = baseURL.dashboardApi;
// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';


const EmailCompose = ({ replyTo, isReplyAll = false, voiceData, onBack, onSent }) => {
    const [formData, setFormData] = useState({
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body: ''
    });
    const [sending, setSending] = useState(false);
    const [autoSend, setAutoSend] = useState(false);

    // Handle voice compose data
    useEffect(() => {
        if (voiceData) {
            setFormData({
                to: voiceData.to || '',
                cc: voiceData.cc || '',
                bcc: voiceData.bcc || '',
                subject: voiceData.subject || '',
                body: voiceData.body || ''
            });

            // Auto-send if flag is set
            if (voiceData.autoSend) {
                setAutoSend(true);
                // Delay to let UI update
                setTimeout(() => {
                    document.getElementById('voice-send-button')?.click();
                }, 1000);
            }
        }
    }, [voiceData]);

    useEffect(() => {
        if (replyTo && !voiceData) {
            let ccRecipients = '';

            // For Reply All, include original TO and CC recipients (excluding self)
            if (isReplyAll) {
                const recipients = [];

                // Add original TO recipient if not replying to yourself
                if (replyTo.to_email && replyTo.to_email !== 'infoxai@infoxwebware.net') {
                    recipients.push(replyTo.to_email);
                }

                // Add original CC recipients if available (would need to be in email data)
                // For now, we'll just use TO recipient as CC

                ccRecipients = recipients.join(', ');
            }

            setFormData({
                to: replyTo.from_email,
                cc: ccRecipients,
                bcc: '',
                subject: replyTo.subject.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject}`,
                body: `\n\n\n--- Original Message ---\nFrom: ${replyTo.from_name} <${replyTo.from_email}>\nDate: ${new Date(replyTo.date).toLocaleString()}\nSubject: ${replyTo.subject}\n\n${replyTo.body || ''}`
            });
        }
    }, [replyTo, isReplyAll]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.to || !formData.subject || !formData.body) {
            toast.error('Please fill in all required fields');
            return;
        }

        setSending(true);
        try {
            if (replyTo) {
                // Send reply
                await axios.post(`${API}/mailbox/reply`, {
                    to: formData.to,
                    subject: formData.subject,
                    body: formData.body,
                    in_reply_to: replyTo.message_id,
                    references: replyTo.message_id
                });
            } else {
                // Send new email
                await axios.post(`${API}/mailbox/compose`, {
                    to: formData.to,
                    cc: formData.cc || undefined,
                    bcc: formData.bcc || undefined,
                    subject: formData.subject,
                    body: formData.body
                });
            }

            toast.success('Email sent successfully');
            onSent();
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error('Failed to send email');
        } finally {
            setSending(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-sm overflow-hidden h-full flex flex-col"
        >
            {/* Header */}
            <div className="p-6 border-b border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={onBack}
                            className="flex items-center space-x-2 text-slate-600 dark:text-slate-400 hover:text-[#0072FF] transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                            <span>Back</span>
                        </button>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                            {replyTo ? (isReplyAll ? 'Reply All to Email' : 'Reply to Email') : 'Compose Email'}
                        </h2>
                    </div>
                    <button
                        id="voice-send-button"
                        onClick={handleSubmit}
                        disabled={sending}
                        className="px-4 py-2 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {sending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>Sending...</span>
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                <span>Send</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* To */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        To <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        name="to"
                        value={formData.to}
                        onChange={handleChange}
                        placeholder="recipient@example.com"
                        required
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-[#0072FF] focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                </div>

                {/* CC & BCC */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            CC {isReplyAll && <span className="text-xs text-slate-500">(Reply All recipients)</span>}
                        </label>
                        <input
                            type="text"
                            name="cc"
                            value={formData.cc}
                            onChange={handleChange}
                            placeholder="cc@example.com"
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-[#0072FF] focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            BCC
                        </label>
                        <input
                            type="text"
                            name="bcc"
                            value={formData.bcc}
                            onChange={handleChange}
                            placeholder="bcc@example.com"
                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-[#0072FF] focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                        />
                    </div>
                </div>

                {/* Subject */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Email subject"
                        required
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-[#0072FF] focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                    />
                </div>

                {/* Body */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        name="body"
                        value={formData.body}
                        onChange={handleChange}
                        placeholder="Type your message here..."
                        rows="12"
                        required
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-[#0072FF] focus:border-transparent bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-none"
                    />
                </div>
            </form>
        </motion.div>
    );
};

export default EmailCompose;
